/** A small line diff for Edit tool previews. LCS for modest inputs, block replace beyond. */

export type DiffLine = { kind: 'ctx' | 'add' | 'del'; text: string }

const MAX_LCS = 600

export function lineDiff(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n')
  const b = newText.split('\n')
  if (a.length * b.length > MAX_LCS * MAX_LCS) {
    return [
      ...a.map((t) => ({ kind: 'del' as const, text: t })),
      ...b.map((t) => ({ kind: 'add' as const, text: t })),
    ]
  }
  // LCS table
  const n = a.length
  const m = b.length
  const dp: Uint16Array[] = []
  for (let i = 0; i <= n; i++) dp.push(new Uint16Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] =
        a[i] === b[j]
          ? dp[i + 1]![j + 1]! + 1
          : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!)
    }
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'ctx', text: a[i]! })
      i++
      j++
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      out.push({ kind: 'del', text: a[i]! })
      i++
    } else {
      out.push({ kind: 'add', text: b[j]! })
      j++
    }
  }
  while (i < n) out.push({ kind: 'del', text: a[i++]! })
  while (j < m) out.push({ kind: 'add', text: b[j++]! })
  return out
}

/** Trim long runs of context to a few lines around each change. */
export function collapseContext(lines: DiffLine[], keep = 2): DiffLine[] {
  const out: DiffLine[] = []
  let run: DiffLine[] = []
  const flush = (atEnd: boolean, atStart: boolean) => {
    if (run.length <= keep * 2 + 1) {
      out.push(...run)
    } else {
      if (!atStart) out.push(...run.slice(0, keep))
      out.push({
        kind: 'ctx',
        text: `… ${run.length - (atStart ? keep : keep * 2)} unchanged lines …`,
      })
      if (!atEnd) out.push(...run.slice(-keep))
    }
    run = []
  }
  let sawChange = false
  for (const l of lines) {
    if (l.kind === 'ctx') run.push(l)
    else {
      flush(false, !sawChange)
      sawChange = true
      out.push(l)
    }
  }
  flush(true, !sawChange)
  return out
}
